import type { Nullable } from "models-webgl2";
import { CommandAction } from "utils/helpers/classes/command-controller";
import { applyCSSToNode } from "utils/dom/apply-css-to-node";
import { removeCSSFromNode } from "utils/dom/remove-css-from-node";
import {
  BrushComponent,
  ImageRectComponent,
  ImageAdjustRectComponent,
} from "../../webgl2-component";
import { Texture } from "../../texture";
import { Webgl2 } from "../../webgl2";
import { Webgl2dRenderer } from "../webgl2d-renderer";
import { Webgl2Tool } from "./webgl2-tool";

export class EraseTool extends Webgl2Tool {
  private _prevTexture: Texture;

  private get _selectedComponent(): Nullable<ImageRectComponent | ImageAdjustRectComponent> {
    const component = this._renderer.selectedComponent;
    return component instanceof ImageRectComponent || component instanceof ImageAdjustRectComponent
      ? component
      : null;
  }

  private get _texture(): Nullable<Texture> {
    return this._selectedComponent?.state.texture;
  }

  private readonly _brushComponent: BrushComponent;

  private readonly _brushDiv: HTMLDivElement;
  public get brushSize() {
    return this._brushComponent.state.u_brushSize;
  }

  public set brushSize(size: number) {
    this._brushComponent.state.u_brushSize = size;
    this._handleScaleChange();
  }

  constructor(private readonly _renderer: Webgl2dRenderer) {
    super();

    this._brushComponent = new BrushComponent({ u_brushSize: 1, u_opacity: 0, u_color: [0, 0, 0] });

    this._brushDiv = document.createElement("div");
    applyCSSToNode(this._brushDiv, {
      position: "absolute",
      top: 0,
      left: 0,
      zIndex: 1,
      pointerEvents: "none",
      boxSizing: "content-box",
      borderRadius: "50%",
      border: "1px solid #000",
      outline: "1px solid #fff",
    });

    this._handleScaleChange = this._handleScaleChange.bind(this);
  }

  public onAssign() {
    applyCSSToNode(this._renderer.parent, { cursor: "none" });
    this._renderer.parent?.appendChild(this._brushDiv);
    this._handleScaleChange();
    this._renderer.camera.events.on("scale", this._handleScaleChange);
  }

  public onUnAssign() {
    removeCSSFromNode(this._renderer.parent, "cursor");
    this._brushDiv.remove();
    this._renderer.camera.events.off("scale", this._handleScaleChange);
  }

  private _handleScaleChange() {
    applyCSSToNode(this._brushDiv, {
      width: this.brushSize * this._renderer.camera.scale + "px",
      height: this.brushSize * this._renderer.camera.scale + "px",
    });
  }

  public onMouseDown(e: MouseEvent) {
    return this._handleDown(e);
  }

  public onMouseMove(e: MouseEvent) {
    return this._handleMove(e);
  }

  public onMouseEnded() {
    return this._handleUp();
  }

  public onMouseOver(e: MouseEvent) {
    applyCSSToNode(this._brushDiv, {
      transform: `translate(calc(-50% + ${e.offsetX}px), calc(-50% + ${e.offsetY}px)) scale(${
        1 / window.devicePixelRatio
      })`,
    });
  }

  public onTouchStart(e: TouchEvent) {
    return this._handleDown(e);
  }

  public onTouchMove(e: TouchEvent) {
    return this._handleMove(e);
  }

  public onTouchEnded() {
    return this._handleUp();
  }

  private _handleDown(e: MouseEvent | TouchEvent) {
    const texture = this._texture;

    if (!this._selectedComponent || !texture) {
      console.warn("EraseTool doesn't have selected component to operate");
      return false;
    }

    this._prevTexture = texture.clone();
    this._brushComponent.transform.setSize(texture.textureSize);

    this._drawToTexture(e);
  }

  private _handleMove(e: MouseEvent | TouchEvent) {
    if (!this._selectedComponent || !this._texture) return;
    this._drawToTexture(e);
  }

  private _handleUp() {
    const component = this._selectedComponent;
    if (!this._texture || !component) return;

    const actionTexture = this._texture.clone();
    const undoTexture = this._prevTexture;

    this._renderer.commands.execute(
      new CommandAction(
        "Eraser Tool",
        () => {
          component.state.texture = actionTexture.clone();
        },
        () => {
          component.state.texture = undoTexture.clone();
        },
      ),
    );
  }

  private _drawToTexture(e: MouseEvent | TouchEvent) {
    if (!this._texture || !this._selectedComponent) return;

    const ratio = this._texture.textureSize.clone().divide(this._selectedComponent.transform.size);
    const currentMouseDown = this._renderer.camera.getGlobalMousePosition(e);
    const localPixel = Webgl2dRenderer.global
      .globalMouseToComponentLocal(currentMouseDown, this._selectedComponent)
      .multiply(ratio);

    this._texture.drawIntoTexture(() => {
      Webgl2.renderComponent(this._brushComponent, {
        u_coords: localPixel,
        u_brushSize: this.brushSize * ratio.x,
      });
    });

    // NOTE: force render to see texture changes since we don't subscribe to it
    // changes because of mutation
    this._renderer.render();
  }
}

export default EraseTool;
